import classNames from 'classnames';
import Scrolls from 'common/components/Accordion/Scrolls';
import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { ForgeCard, ForgeDivider, ForgeOpenIcon } from '@tylertech/forge-react';
import './AccordionPaneV2-stylesheet.scss';

interface IAccordionPaneV2Props extends PropsWithChildren<any> {
  className?: string;
  title: JSX.Element | string;
  onToggle?: (paneId: string) => void;
  onOpen?: () => void;
  isOpen?: boolean;
  paneId?: string;
  toggleOnCaretOnly?: boolean;
}

const DEFAULT_TRANSITION_DURATION = 400;
const DEFAULT_CONTENT_TRANSITION = `height ${DEFAULT_TRANSITION_DURATION}ms ease 0s, opacity ${DEFAULT_TRANSITION_DURATION}ms ease 0s`;
const DEFAULT_BUTTON_TRANSITION = `transform ${DEFAULT_TRANSITION_DURATION}ms ease 0s`;

function setTransitionIfNeeded(styleRef: CSSStyleDeclaration, animate: boolean, defaultTransition: string) {
  const transition = styleRef.transition;
  if (animate) {
    if (transition !== defaultTransition) styleRef.transition = defaultTransition;
  } else styleRef.transition = '';
}

const togglePane = (contentRef: HTMLDivElement | null, isOpen: boolean, needsAnimation: boolean) => {
  const contentHeight = contentRef?.scrollHeight;
  const styleRef = contentRef?.style;
  if (styleRef) {
    if (isOpen) {
      styleRef.height = `${contentHeight}px`;
      styleRef.overflow = 'hidden';
      setTransitionIfNeeded(styleRef, needsAnimation, DEFAULT_CONTENT_TRANSITION);
      setTimeout(() => {
        styleRef.height = '0px';
      }, 0);
    } else {
      styleRef.height = `${contentHeight}px`;
      setTransitionIfNeeded(styleRef, needsAnimation, DEFAULT_CONTENT_TRANSITION);
      setTimeout(() => {
        contentRef?.style?.removeProperty('height');
        contentRef?.style?.removeProperty('overflow');
        contentRef?.style?.removeProperty('opacity');
      }, DEFAULT_TRANSITION_DURATION);
    }
  }
};

function focusCurrentCustomizationTab() {
  const tab = document.querySelector('.tab-link.current a') as HTMLAnchorElement;
  tab?.focus?.();
}

const InternalAccordionPane = (props: IAccordionPaneV2Props) => {
  const { title, className, toggleOnCaretOnly, isOpen, paneId, onOpen, onToggle } = props;

  const contentRef = useRef<HTMLDivElement>(null);
  const paneTitleElement = useRef<HTMLDivElement>(null);
  const needsAnimation = useRef(false);

  const toggle = () => {
    if (!isOpen) onOpen?.call(this);
    needsAnimation.current = true;
    onToggle?.call(this, [paneId]);
  };

  useEffect(() => {
    const animate = needsAnimation.current;
    togglePane(contentRef.current, !isOpen, animate);
    needsAnimation.current = false;
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const firstPaneTitle = document.querySelector(
      '.customization-tab-pane:not(.customization-tab-pane_hidden) .paneSubHeader',
    );
    const isFirstPaneTitle = paneTitleElement.current === firstPaneTitle;
    const isShiftTab = event.shiftKey && event.keyCode === 9;

    if (event.keyCode == 13 || event.keyCode == 32) {
      onToggle?.(paneId!);
      event.preventDefault();
    } else if (isShiftTab && isFirstPaneTitle) {
      focusCurrentCustomizationTab();
      event.preventDefault();
    }
  };

  const handleClick = () => {
    if (!toggleOnCaretOnly) {
      toggle();
    }
  };

  const handleOpenIconClick = () => {
    if (toggleOnCaretOnly) {
      toggle();
    }
  };

  return (
    <>
      {/* The Accordion applies a border style and so does the card so we set the style to none so we don't have a thick border */}
      <ForgeCard className="paneRoot" style={{ '--forge-card-outline-style': 'none' }}>
        <div className={classNames(className, paneId, isOpen && 'open', 'socrata-accordion-pane')}>
          <div
            ref={paneTitleElement}
            slot="header"
            onClick={handleClick}
            role="button"
            aria-expanded={isOpen ? 'true' : 'false'}
            aria-label={
              props['aria-label'] ||
              // Title here can be an Element. This probably won't print nicely in that case
              title
            }
            onKeyDown={handleKeyDown}
            className={'paneSubHeader forge-typography--body2'}
          >
            <span className="paneTitle">{title}</span>
            <ForgeOpenIcon
              id="skip-link-target-main"
              tabIndex={0}
              open={!!isOpen}
              onclick={handleOpenIconClick}
            />
          </div>
          <div ref={contentRef} className={'socrata-accordion-pane-content'}>
            <div>{props.children}</div>
          </div>
        </div>
      </ForgeCard>
      <ForgeDivider />
    </>
  );
};

const ScrolledAccordionPane = Scrolls(InternalAccordionPane);

export const AccordionPaneV2 = (props: IAccordionPaneV2Props) => {
  return <ScrolledAccordionPane {...props} />;
};
